<template>
  <!-- O texto é o link para o ambiente; o nível e o Configurar ficam fora dele, ao lado — um botão
       dentro de um link é marcação inválida, e o clique no ⚙ entraria no ambiente. -->
  <div class="workspace-row" :class="{'workspace-row--closed': system.closed}">
    <router-link class="workspace-text" :to="{name: 'view-system', params: {system: system.alias}}">
      <span class="workspace-title text-subtitle-1">{{ system.title }}</span>
      <!-- A descrição é markdown: numa linha, como no card do kanban, na fonte da linha -->
      <ontos-markdown-excerpt v-if="description" :value="description"
                              class="workspace-description text-body-2 text-medium-emphasis"/>
      <!-- As coleções e os números chegam depois da lista, com o resumo, e entram acendendo — o mesmo
           da página de entrada do ambiente. O `key` pelo texto é o que refaz a animação quando ele
           chega. As coleções numa linha própria, acima dos números (escolha do usuário). -->
      <span v-if="collectionNames" :key="collectionNames"
            class="workspace-collections text-body-2 text-medium-emphasis ontos-arrives">
        {{ collectionNames }}
      </span>
      <span v-if="numbers" :key="numbers" class="workspace-numbers text-caption text-medium-emphasis ontos-arrives">
        {{ numbers }}
      </span>
    </router-link>
    <ontos-colored-value v-if="system.accessLevel" :option="levelOption(system.accessLevel)" form="chip"
                         class="workspace-level"/>
    <v-btn v-if="designs" icon="mdi-tune" variant="text" size="small" class="workspace-design"
           :title="$tt('design-of', {name: system.title})" :aria-label="$tt('design-of', {name: system.title})"
           :to="{name: 'design-system', params: {system: system.alias}}"/>
  </div>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import ColoredValue from '@/components/shared/ColoredValue.vue'
import MarkdownExcerpt from '@/components/shared/markdown/MarkdownExcerpt.vue'
import {AccessLevel, allows, levelOption} from '@/types/AccessLevel'
import {WorkspaceOverview} from '@/types/WorkspaceSummary'
import {relativeTime} from '@/utils/relativeTime'
import {Described, mentionScopeOf, shownDescription} from '@/utils/markdownMentions'
import {WORKSPACE} from '@/components/shared/WorkspaceRouteComponent'
import {computed} from 'vue'

/** Um ambiente como a lista da tela inicial o mostra — o que ela precisa dele. */
export type HomeWorkspace = Described & { alias: string, title: string, closed?: boolean, accessLevel?: AccessLevel }

/**
 * Um ambiente na tela inicial: o nome, a descrição, o seu nível nele (na cor do nível), os números e
 * quando mudou — e o Configurar, para quem desenha ali (escolha do usuário). Na lista dos abertos e no
 * grupo dos encerrados, que o esmaece.
 */
@Component({name: 'ontos-workspace-row', components: {'ontos-colored-value': ColoredValue,
    'ontos-markdown-excerpt': MarkdownExcerpt},
  //O resumo da descrição desenha as referências pelo ambiente que recebe, como numa tela de ambiente: aqui,
  //o mínimo que a descrição cita (ver `mentionScopeOf`)
  provide(this: WorkspaceRow) {
    return {[WORKSPACE]: computed(() => mentionScopeOf(this.system))}
  }})
class WorkspaceRow extends Vue {
  @Prop({type: Object, required: true}) readonly system!: HomeWorkspace
  /** O resumo, que chega depois da lista; até lá, a linha sem os números. */
  @Prop({type: Object, default: null}) readonly overview!: WorkspaceOverview | null

  //exposta ao template: função de módulo não é enxergada de lá
  levelOption = levelOption

  /** A descrição como se mostra, com as referências desenhadas — ver `shownDescription`. */
  get description(): string {
    return shownDescription(mentionScopeOf(this.system), this.system)
  }

  /** O nível é de cada ambiente, e não da conta: administrador num e visualizador em outro. */
  get designs(): boolean {
    return allows(this.system.accessLevel, 'designer')
  }

  /** "Demandas · Tarefas · Sprints" — as coleções que quem olha vê ali. */
  get collectionNames(): string {
    return this.overview ? this.overview.collections.join(' · ') : ''
  }

  /** "5 coleções · 312 itens · há 2 h" — o que quem olha vê ali, e quando mudou por último. */
  get numbers(): string {
    if (!this.overview) return ''
    const {collections: {length: collections}, items, updatedAt} = this.overview
    return [
      this.$tc('collection-count', collections, {count: collections}),
      this.$tc('item-count', items, {count: items}),
      ...(updatedAt ? [relativeTime(updatedAt)] : [])
    ].join(' · ')
  }
}

export default toNative(WorkspaceRow)
</script>

<style scoped>
/* O nível e o ⚙ à direita, só na linha do nome; as linhas de baixo — descrição, coleções, números —
   na largura inteira. Com o nível e o ⚙ numa coluna da altura do item, as de baixo cortavam com
   reticências no meio da tela, com espaço sobrando (relato do usuário). */
.workspace-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: start;
  column-gap: 8px;
  padding: 12px 4px;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.workspace-row:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.04);
}

/* O encerrado se lê, mas recua: nada muda mais nele */
.workspace-row--closed {
  opacity: 0.6;
}

/* O link na largura inteira, com as colunas da linha (`subgrid`): o nome na primeira, ao lado do
   nível e do ⚙, e as linhas de baixo atravessando todas. O link continua um só — o item inteiro é
   clicável —, e o nível e o ⚙ continuam fora dele, por cima, na linha do nome. */
.workspace-text {
  grid-column: 1 / -1;
  grid-row: 1;
  display: grid;
  grid-template-columns: subgrid;
  row-gap: 2px;
  min-width: 0;
  color: inherit;
  text-decoration: none;
}

.workspace-text > * {
  grid-column: 1 / -1;
  min-width: 0;
}

.workspace-text > .workspace-title {
  grid-column: 1;
  font-weight: 500;
}

/* Numa linha só, como a descrição: com muitas coleções, corta com reticências */
.workspace-description,
.workspace-collections {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.workspace-level {
  grid-column: 2;
  grid-row: 1;
  margin-top: 2px;
}

.workspace-design {
  grid-column: 3;
  grid-row: 1;
  margin-top: -4px;
}
</style>
